import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation, Trans } from 'react-i18next';
import { AlertService } from '@common/services/alert';
import { pageWrapper, EEventNames } from '../../../../../client/analytics';
import Container from '../../../../common/components/container';
import Overview from '../../../../common/components/overview';
import Permission from '../../../../common/components/permission';
import { Table, TableDropdown } from '../../../../common/components/table';
import Confirm from '../../../../common/components/confirm-button';
import Icon from '../../../../common/components/icon';
import { Button } from '../../../../common/components/button';
import TeamModal from '../../components/team';
import { startEdit, stopEdit } from '../../actions/update-team';
import deleteTeam from '../../actions/remove-team';
import TeamForm, { TeamSyncLabel, getTeamSyncLabelData } from '../../forms/team';
import { EPermissions } from '../../../../common/definitions';
import * as networkSelector from '../../selectors/network';
import * as teamsSelector from '../../selectors/teams';
import Api from '../../../../common/services/api';

const columns = [
  {},
  { className: 'Table__Cell--clickable' },
];

class NetworkSettingsTeams extends Component {
  static propTypes = {
    network: PropTypes.object.isRequired,
    teams: PropTypes.array.isRequired,
    functions: PropTypes.array.isRequired,
    startEdit: PropTypes.func.isRequired,
    stopEdit: PropTypes.func.isRequired,
    deleteTeam: PropTypes.func.isRequired,
    team: PropTypes.object,
    t: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      showTeamModal: false,
      teamList: this.props.teams,
      order: this.props.teams.map((team) => team.id),
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.teams !== this.props.teams) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        teamList: this.props.teams,
        order: this.props.teams.map((team) => team.id),
      });
    }
  }

  handleOrderChange = (sourceId, targetId) => {
    const { teamList, order } = this.state;
    const index = order.findIndex((orderId) => orderId === targetId);
    const newOrder = [
      ...order.filter((id) => id !== sourceId)
    ];
    newOrder.splice(index, 0, sourceId);
    const sortedTeams = teamList.slice().sort((a, b) => {
      return newOrder.indexOf(a.id) - newOrder.indexOf(b.id);
    });
    this.setState({ order: newOrder, teamList: sortedTeams });
  };

  handleDrop = async () => {
    const { order } = this.state;
    try {
      const { network } = this.props;
      await Api.put(`/v1/networks/${network.id}/teams`, {
        teams: order,
      });
      AlertService.success(<Trans i18nKey="learning:saved_order" />);
    } catch (response) {
      AlertService.forStatus(response.status_code, {
        warning: <Trans i18nKey="learning:warning_saving_order" />,
        error: <Trans i18nKey="learning:error_saving_order" />,
      });
    }
  };

  renderRow({ item }) {
    const syncData = getTeamSyncLabelData(item);
    return [
      <>
        { item.name }
        {
          syncData && (
            <small className="AlertText">
              <Icon type="sync_alt" />
              <span>
                <TeamSyncLabel {...syncData} />
              </span>
            </small>
          )
        }
      </>,
      <TeamModal team={item}>
        <small>
          <Trans
            i18nKey="network:settings_teams_row_employee_count"
            values={{ count: item.member_count }}
          />
        </small>
      </TeamModal>,
    ];
  }

  render() {
    const { showTeamModal, teamList } = this.state;
    const {
      network, teams, functions, team, t,
    } = this.props;

    const teamForm = showTeamModal ? (
      <TeamForm
        form={team ? `edit-team-${team.id}` : 'create-team'}
        network={network}
        teams={teams}
        team={team}
        functions={functions}
        onClose={() => {
          if (team) this.props.stopEdit();
          this.setState({ showTeamModal: false });
        }}
      />
    ) : null;

    return (
      <Container name="SettingsAdmins">
        <Container.Content>
          <Overview>
            {teamForm}
            <Overview.Header>
              <Overview.Header.Title>
                <Trans i18nKey="network:settings_teams_title" />
              </Overview.Header.Title>
              <Permission name={EPermissions.NETWORK_TEAMS_CREATE}>
                <div className="pull-right">
                  <Button type="primary" size="large" iconRight="add" onClick={() => this.setState({ showTeamModal: true })}>
                    <Trans i18nKey="network:settings_teams_create_button" />
                  </Button>
                </div>
              </Permission>
            </Overview.Header>
            <Overview.Content>
              <Table
                hideHeader
                columns={columns}
                items={teamList}
                renderRow={this.renderRow}
                placeholder={(
                  <div className="Table__Placeholder">
                    <Trans i18nKey="network:settings_teams_placeholder" />
                  </div>
                )}
                ActionComponent={({ item }) => (
                  <Permission
                    name={[
                      EPermissions.NETWORK_TEAMS_UPDATE,
                      EPermissions.NETWORK_TEAMS_REMOVE
                    ]}
                  >
                    <TableDropdown id="team-actions-dropdown">
                      <Permission name={EPermissions.NETWORK_TEAMS_UPDATE}>
                        <TableDropdown.Item
                          onClick={() => {
                            this.props.startEdit(item.id);
                            this.setState({ showTeamModal: true });
                          }}
                        >
                          {
                            item.is_synced ?
                              t('network:team_actions_view') :
                              t('network:team_actions_edit')
                          }
                        </TableDropdown.Item>
                      </Permission>
                      <Permission name={EPermissions.NETWORK_TEAMS_REMOVE}>
                        <Confirm
                          danger
                          title={t('network:team_actions_delete_confirm_title')}
                          onConfirm={() => this.props.deleteTeam(item.id)}
                          confirmText={t('network:team_actions_delete_confirm_button')}
                        >
                          <TableDropdown.Item danger>
                            <Trans i18nKey="network:team_actions_delete_button" />
                          </TableDropdown.Item>
                        </Confirm>
                      </Permission>
                    </TableDropdown>
                  </Permission>
                )}
                onChangeOrder={this.handleOrderChange}
                onDrop={this.handleDrop}
              />
            </Overview.Content>
          </Overview>
        </Container.Content>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  network: networkSelector.selected(state),
  teams: networkSelector.teams(state),
  functions: networkSelector.functions(state),
  team: teamsSelector.editing(state),
});
const mapDispatchToProps = {
  startEdit,
  stopEdit,
  deleteTeam,
};

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps,
)(pageWrapper(EEventNames.VISITED_NETWORK_TEAMS_SETTINGS_PAGE)(NetworkSettingsTeams)));
